import type React from 'react';

import { Button } from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { usePostHog } from 'posthog-js/react';

import { FrameworkDetailsDialog } from './FrameworkDetailsDialog';
import { LeavePageWarningDialog } from './LeavePageWarningDialog';

import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useConversation,
  type ProviderType,
  type TranscriptEntry,
} from '../../../hooks/useConversation';
import { useTTS } from '../../../hooks/useTTS';
import type { Language, VoiceRoleplaySession } from '../../../types/session';
import {
  DEFAULT_PERSONA,
  PERSONA_OPTIONS,
  SessionPhase,
  SUPPORTED_LANGUAGES,
} from '../../../utils/constants';
import api from '../../../utils/ky';
import { AnalysisModal } from '../../role-play/session-feedback/analysis-modal';
import { SessionFeedbackLoading } from './SessionFeedbackLoading';
import { VoiceWaves } from './voice-waves';
import LanguageSelector from '../../../components/LanguageSelector';
import { useSessionPhase } from '@/hooks/useSessionPhase';

// TTS WebSocket URL
const TTS_WEBSOCKET_URL =
  import.meta.env.VITE_TTS_WEBSOCKET_URI || 'wss://aibot.hupo.co/tts';

interface EndSessionResponse {
  success: boolean;
  slackId: string;
  incomplete?: boolean;
  isBrief?: boolean;
}

// Helper to safely access session data in any format
const getSessionValue = <T,>(
  session: VoiceRoleplaySession,
  paths: string[],
  defaultValue: T,
): T => {
  let current: any = session;
  for (const path of paths) {
    if (current === undefined || current === null) return defaultValue;
    current = current[path];
  }
  return current !== undefined && current !== null ? current : defaultValue;
};

const getSessionPhaseData = (phase: SessionPhase) => {
  switch (phase) {
    case SessionPhase.PRE_START:
      return { label: '', color: '' };
    case SessionPhase.CONNECTING:
      return { label: 'Connecting...', color: 'bg-[#5FA5F9]' };
    case SessionPhase.INTRO:
      return { label: 'Introduction', color: 'bg-[#5FA5F9]' };
    case SessionPhase.MAIN_CONVERSATION:
      return { label: 'Main Conversation', color: 'bg-[#5FA5F9]' };
    case SessionPhase.ENDING:
      return { label: 'Ending...', color: 'bg-[#5FA5F9]' };
    default:
      return { label: '', color: '' };
  }
};

interface VoiceSessionProps {
  session: VoiceRoleplaySession;
  startTimeRef: React.MutableRefObject<number>;
  leavePageWarning: boolean;
  setLeavePageWarning: React.Dispatch<React.SetStateAction<boolean>>;
  provider?: ProviderType;
  onLanguageChange?: (languageCode: string) => void;
}

export function VoiceSession({
  session,
  startTimeRef,
  leavePageWarning,
  setLeavePageWarning,
  provider = '11labs',
  onLanguageChange,
}: VoiceSessionProps) {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFrameworkModalOpen, setIsFrameworkModalOpen] = useState(false);
  const [isSkipButtonClicked, setIsSkipButtonClicked] = useState(false);
  const [persona, setPersona] = useState<any>('');

  const isIntroCompletedRef = useRef(false);
  const introAudioCompleteRef = useRef(false);
  const practiceStartedRef = useRef(false);

  const { sessionPhaseRef, setPhase } = useSessionPhase();

  // Initialize WebSocket for TTS
  const ttsSocketRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    const ttsSocket = new WebSocket(TTS_WEBSOCKET_URL);

    ttsSocket.onopen = () => {
      console.log('TTS WebSocket connected for intro');
    };

    ttsSocket.onerror = (error) => {
      console.error('TTS WebSocket error:', error);
    };

    ttsSocketRef.current = ttsSocket;

    return () => {
      ttsSocket.close();
    };
  }, []);

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const queryString = new URLSearchParams(window.location.search).toString();

  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null,
  );
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [isDimmed, setIsDimmed] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  useEffect(() => {
    // Get personaId from either aiConfig or roleplay object using helper function
    const personaId =
      getSessionValue(session, ['aiConfig', 'personaId'], null) ||
      getSessionValue(session, ['roleplay', 'personaId'], DEFAULT_PERSONA);

    if (personaId) {
      // Find matching persona in PERSONA_OPTIONS
      const foundPersona = PERSONA_OPTIONS.find((p) => p.id === personaId);
      setPersona(foundPersona || null);
    }
  }, [session]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    setIsDimmed(showAnalysisModal);
  }, [sessionId, showAnalysisModal]);

  useEffect(() => {
    if (session) {
      console.log(`Session updated, language: ${session.language}`);

      // Build list of available languages
      let languages: Language[] = [];

      // Use session.languages if available, otherwise use default list
      if (session.languages?.length) {
        languages = session.languages;
      } else {
        languages = [{ code: 'en', name: 'English', isDefault: true }];
      }

      setAvailableLanguages(languages);

      // ALWAYS prioritize session.language - this is the key change
      if (session.language) {
        // Try to find the language in our available languages
        const matchedLanguage = languages.find(
          (lang) => lang.code === session.language,
        );

        if (matchedLanguage) {
          console.log(
            `Found matching language for ${session.language}: ${matchedLanguage.name}`,
          );
          setSelectedLanguage(matchedLanguage);
        } else {
          // If not found, create a new language entry
          const langName = getLangNameFromCode(session.language);
          const newLanguage = {
            code: session.language,
            name: langName,
            isDefault: false,
          };

          console.log(
            `Created new language for ${session.language}: ${langName}`,
          );
          setSelectedLanguage(newLanguage);

          // Also add to available languages
          setAvailableLanguages((prev) => [...prev, newLanguage]);
        }
      } else {
        // No session.language - use default
        const defaultLanguage =
          languages.find((lang) => lang.isDefault) || languages[0];
        setSelectedLanguage(defaultLanguage);
      }
    }
  }, [session]);

  const { mutate: endSession, isPending: endSessionIsProcessing } = useMutation(
    {
      mutationFn: async ({
        sessionId,
        transcript,
        duration,
      }: {
        sessionId: string;
        transcript: string;
        duration: number;
      }) => {
        const response = await api.post('api/sessions/voice/end-session', {
          json: { sessionId: session.id, transcript, duration },
          timeout: 30000,
        });

        return response.json<EndSessionResponse>();
      },
      onSuccess: (data, variables) => {
        if (data.incomplete) {
          // Show analysis modal for incomplete transcripts
          setShowAnalysisModal(true);
          setPhase(SessionPhase.PRE_START);
          startTimeRef.current = 0;
        } else {
          console.log('Session ended successfully');
          posthog.capture('Role Play Session Ended', {
            sessionId: variables.sessionId,
            sessionType: 'voice',
            source: 'openai (role play)',
            duration: variables.duration,
          });

          onSuccessfulEnd();
        }
        posthog.capture('session_ended', {
          medium: 'voice',
          isRoleplay: true,
          sessionId: session.id,
        });
      },
      onError: (error) => {
        console.error('Failed to end roleplay:', error);
        toast.error('Failed to end roleplay');
        posthog.capture('session_end_error', {
          medium: 'voice',
          isRoleplay: true,
          sessionId: session.id,
          error,
        });
      },
    },
  );

  const handleTransitionToMain = async () => {
    if (!isIntroCompletedRef.current) return;
    try {
      await connectConversation();
      setPhase(SessionPhase.MAIN_CONVERSATION);
    } catch (error) {
      console.error('Error transitioning to main conversation:', error);
      toast.error('Failed to start main conversation');
    }
  };

  // Use our updated TTS hook with the WebSocket
  const { playIntro } = useTTS({
    socket: ttsSocketRef.current!,
    setIsAISpeaking,
    onIntroComplete: handleTransitionToMain,
    introAudioCompleteRef,
    isIntroCompletedRef,
  });

  const onSuccessfulEnd = () => {
    navigate(`/en/session-feedback?${queryString}`);
    window.location.reload();
  };

  // Helper to convert language code to display name
  const getLangNameFromCode = (code: string): string => {
    // Find the language in SUPPORTED_LANGUAGES
    const language = SUPPORTED_LANGUAGES.find((lang) => lang.code === code);

    // Return the name if found, otherwise fall back to the code itself
    return language ? language.name : code;
  };

  // Handle language change
  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);
    console.log(`Language selected in component: ${language.code}`);

    // Call parent callback with language code
    if (onLanguageChange) {
      onLanguageChange(language.code);
    }
  };

  // Get language from selected language or session
  const getSessionLanguage = () => {
    if (selectedLanguage) return selectedLanguage.code;
    return getSessionValue(session, ['language'], 'en');
  };

  // Get voiceId from session or use mapping based on language
  const getVoiceId = () => {
    // First try to get explicit voiceId from session
    const explicitVoiceId = getSessionValue(session, ['voice', 'voiceId'], '');
    if (explicitVoiceId) return explicitVoiceId;

    // Otherwise map from language
    const langCode = getSessionLanguage();
    const LANGUAGE_TO_VOICE_ID = {
      en: 'tpFVjZdYBvOaWaUIRHRz', // Adam
      id: 'XrExE9yKIg1WjnnlVkGX', // Indonesian voice
      zh: 'ByhETIclHirOlWnWKhHc', // Chinese voice
      ja: '8EkOjt4xTPGMclNlh1pk', // Japanese voice
      ko: 'z6Kj0hecH20CdetSElRT', // Korean voice
    };

    return LANGUAGE_TO_VOICE_ID[langCode] || '';
  };

  const voiceId = getVoiceId();
  const language = getSessionLanguage();

  const { connectConversation, disconnectConversation, transcript } =
    useConversation(provider, {
      session,
      startTimeRef,
      setIsAISpeaking,
      isIntroCompletedRef,
      setError,
      voiceId,
      language,
    });
  const handleStartPractice = async () => {
    try {
      setPhase(SessionPhase.CONNECTING);
      await navigator.mediaDevices.getUserMedia({ audio: true });
      practiceStartedRef.current = true;
      // Get intro text from session
      const introText = session.voice.firstMessage.intro;
      // Use selected language or fallback to session language
      const langCode = selectedLanguage?.code || getSessionLanguage();

      // Check if TTS socket is open
      if (
        ttsSocketRef.current &&
        ttsSocketRef.current.readyState === WebSocket.OPEN
      ) {
        // Play intro using TTS socket
        playIntro(session.id, introText, language);
        setPhase(SessionPhase.INTRO);
        setTimeout(() => setIsAISpeaking(true), 1000);
      } else {
        console.error(
          'TTS socket is not open, falling back to direct connection',
        );
        // Skip intro and connect directly to main conversation
        isIntroCompletedRef.current = true;
        await handleTransitionToMain();
      }

      posthog.capture('voice_roleplay_started', {
        sessionId: session.id,
        scenario:
          getSessionValue(session, ['context', 'scenario', 'title'], '') ||
          getSessionValue(session, ['scenario', 'title'], ''),
        language: langCode,
      });
    } catch (error) {
      console.error('Error starting practice:', error);
      toast.error(
        'Failed to start practice. Please check your microphone permissions.',
      );
    }
  };

  const handleSkipIntro = useCallback(async () => {
    setIsAISpeaking(false);
    setIsSkipButtonClicked(true);

    // Stop audio playback if it's playing
    if (
      ttsSocketRef.current &&
      ttsSocketRef.current.readyState === WebSocket.OPEN
    ) {
      // Could send a stop command to the TTS service if supported
    }

    // If we have an audio element reference, stop it
    const audioElement = document.querySelector('audio');
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    isIntroCompletedRef.current = true;
    await handleTransitionToMain();
  }, [handleTransitionToMain]);

  const handleEndPractice = useCallback(async () => {
    setPhase(SessionPhase.ENDING);
    const formattedTranscript = transcript.map((msg: TranscriptEntry) => ({
      role: msg.source,
      content: msg.message,
    }));
    setIsAISpeaking(false);
    await disconnectConversation();
    await endSession({
      sessionId: session.id,
      transcript: JSON.stringify(formattedTranscript),
      duration: (Date.now() - startTimeRef.current) / 1000,
    });
  }, [transcript, endSession, disconnectConversation]);

  const viewFrameworkDetails = () => {
    setIsFrameworkModalOpen(true);
    posthog.capture('framework_details_viewed', {
      sessionId: session.id,
      medium: 'voice',
      framework: session.context.framework.title,
    });
  };

  const sessionPhaseData = getSessionPhaseData(sessionPhaseRef.current);

  return (
    <>
      <FrameworkDetailsDialog
        isOpen={isFrameworkModalOpen}
        onClose={() => setIsFrameworkModalOpen(false)}
        title={session.context.framework.title}
        content={session.context.framework.detailedGuide}
      />

      <LeavePageWarningDialog
        isOpen={leavePageWarning}
        onClose={() => setLeavePageWarning(false)}
        onConfirm={() => {
          setLeavePageWarning(false);
          handleEndPractice();
        }}
      />

      {endSessionIsProcessing && <SessionFeedbackLoading />}

      <div className="flex h-full">
        <div className="flex h-full w-[27rem] flex-col gap-4 border-r border-[#e0e0e0] p-7">
          <p className="text-sm/5 tracking-tight">
            {sessionPhaseData.label && (
              <>
                <span
                  className={clsx(
                    'mr-2 inline-block size-3 rounded-full',
                    sessionPhaseData.color,
                  )}
                ></span>
                {sessionPhaseData.label}
              </>
            )}
          </p>

          <h3 className="text-xl/7 font-[600] tracking-[-0.02em]">
            {getSessionValue(session, ['context', 'scenario', 'title'], '') ||
              getSessionValue(session, ['scenario', 'title'], '')}
          </h3>

          <div className="space-y-3 rounded-xl border border-[#E0E0E0] p-4">
            <h4 className="text-sm/5 font-bold tracking-tight">Objective</h4>

            <p className="text-sm/5 tracking-tight text-black/60">
              {session.context.scenario.objective}
            </p>
          </div>

          <div className="space-y-3 rounded-xl border border-[#E0E0E0] p-4">
            <h4 className="text-sm/5 font-bold tracking-tight">
              Conversation Partner Persona
            </h4>

            {/* Use the persona object directly if available, otherwise display personaId */}
            {(persona ||
              getSessionValue(session, ['aiConfig', 'personaId'], null)) && (
              <p className="text-sm/5 tracking-tight text-black/60">
                <strong>
                  {persona?.title ||
                    getSessionValue(session, ['aiConfig', 'personaId'], '')}
                </strong>
                {persona?.description && ` - ${persona.description}`}
              </p>
            )}
          </div>

          <div className="space-y-3 rounded-xl border border-[#E0E0E0] p-4">
            <h4 className="text-sm/5 font-bold tracking-tight">Framework</h4>

            <h4 className="text-sm/5 font-bold tracking-tight">
              {session.context.framework.title}
            </h4>

            <p className="text-sm/5 tracking-tight text-black/60">
              {session.context.framework.description}
            </p>

            <p className="text-sm/5 tracking-tight">
              <button
                className="font-bold text-primary"
                onClick={viewFrameworkDetails}
              >
                View framework details
              </button>
            </p>
          </div>
        </div>

        <div className="relative flex max-h-full min-h-full w-full flex-col justify-between bg-[#F9FAFB] px-4 pb-0 pt-4">
          <div className="space-y-3 rounded-xl border border-[#E0E0E0] bg-white p-4">
            <h4 className="text-base/6 font-bold tracking-tight">Scenario</h4>

            <p className="text-sm/5 tracking-tight text-black/60">
              {session.context.scenario.description}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-11">
            <AiAvatarSpeaking isAISpeaking={isAISpeaking} />

            {/* Language selector and start button container */}
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <LanguageSelector
                languages={availableLanguages}
                defaultLanguage={selectedLanguage || undefined}
                onChange={handleLanguageChange}
                disabled={sessionPhaseRef.current !== SessionPhase.PRE_START} // Disable when not in PRE_START phase
              />

              {sessionPhaseRef.current === SessionPhase.PRE_START && (
                <Button
                  className="w-[16rem] rounded-full bg-[#ff4b0a] px-4 py-3 text-base tracking-tight text-white hover:bg-[#e60d00] focus:outline-none focus:ring-2 focus:ring-[#ff4b0a] focus:ring-offset-2"
                  onClick={handleStartPractice}
                >
                  Start practice
                </Button>
              )}
            </div>
          </div>

          <div className="-mx-4 flex items-center justify-between border-[#70E0E0] bg-white px-6 py-4">
            {sessionPhaseRef.current === SessionPhase.CONNECTING ? (
              <Button
                className="rounded-full bg-primary px-4 py-2.5 text-sm/5 tracking-tight text-white data-[hover]:bg-primary-600 data-[focus]:outline-1 data-[focus]:outline-primary"
                disabled={true}
              >
                Connecting...
              </Button>
            ) : (
              sessionPhaseRef.current !== SessionPhase.PRE_START && (
                <div className="space-x-4">
                  <Button
                    className="rounded-full border border-[#e0e0e0] px-4 py-2.5 text-sm/5 tracking-tight disabled:bg-[#E0E0E0] disabled:text-[#909090]"
                    onClick={handleSkipIntro}
                    disabled={
                      isIntroCompletedRef.current || isSkipButtonClicked
                    }
                  >
                    Skip introduction
                  </Button>

                  <Button
                    className="rounded-full bg-[#F6261A] px-4 py-2.5 text-sm/5 tracking-tight text-white data-[hover]:bg-primary-600 data-[focus]:outline-1 data-[focus]:outline-primary"
                    onClick={handleEndPractice}
                  >
                    End session
                  </Button>
                </div>
              )
            )}

            {sessionPhaseRef.current !== SessionPhase.PRE_START &&
              sessionPhaseRef.current !== SessionPhase.CONNECTING && (
                <p className="text-base/6 tracking-tight">
                  <span
                    className={clsx(
                      'inline-flex h-3 w-3 rounded-full',
                      isAISpeaking ? 'bg-green-500' : 'bg-gray-300',
                    )}
                  >
                    <span
                      className={clsx(
                        'inline-flex h-full w-full animate-ping rounded-full',
                        isAISpeaking ? 'bg-green-400 opacity-75' : 'opacity-0',
                      )}
                    />
                  </span>
                  {isAISpeaking ? '  AI Speaking' : '  AI Listening'}
                </p>
              )}
          </div>
        </div>
        {isDimmed && <div className="fixed inset-0 bg-black/50 z-40" />}
        <AnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => {
            setShowAnalysisModal(false);
            setIsDimmed(false);
            window.location.reload();
          }}
        />
      </div>
    </>
  );
}

const AiAvatarSpeaking = ({ isAISpeaking }: { isAISpeaking: boolean }) => (
  <div className="relative flex flex-1 items-center justify-center">
    <motion.div
      animate={
        isAISpeaking
          ? {
              scale: [1, 1.05, 1],
              transition: { duration: 2, repeat: Number.POSITIVE_INFINITY },
            }
          : {}
      }
    >
      <div className="relative">
        <img
          src="https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp"
          alt="AI Coach"
          className="size-48 rounded-full shadow-lg"
        />
        {isAISpeaking && (
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-primary"
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0.5] }}
            transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
          />
        )}
        {isAISpeaking && <VoiceWaves volume={100} />}
      </div>
    </motion.div>
  </div>
);
